import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Icon } from '../../../../layout/Icon.jsx';
import { api, apiError } from '../../../../shared/api.js';
import { toast } from '../../../../shared/useToast.js';
import { usePermissions } from '../../hooks/usePermissions.js';
import { PermissionMatrix } from '../roles/PermissionMatrix.jsx';

// شاشة تحديد الصلاحيات الفردية لمستخدم بعينه. تفتح بعد إنشاء/تحديد المستخدم.
export function UserPermissionsModal({ open, user, onClose }) {
  const qc = useQueryClient();
  const userId = user?._id || user?.id || null;
  const { data: groups = [] } = usePermissions();
  const [selected, setSelected] = useState([]);
  const [saving, setSaving] = useState(false);

  const userPermsQuery = useQuery({
    queryKey: ['users', userId, 'permissions'],
    enabled: !!userId && open,
    queryFn: async () => {
      const { data } = await api.get(`/users/${userId}/permissions`);
      return data;
    }
  });

  useEffect(() => {
    if (userPermsQuery.data) {
      setSelected(Array.isArray(userPermsQuery.data.permissions) ? userPermsQuery.data.permissions : []);
    }
  }, [userPermsQuery.data]);

  const totalCount = useMemo(
    () => groups.reduce((sum, g) => sum + ((g.perms || g.permissions || []).length), 0),
    [groups]
  );

  function toggle(key) {
    setSelected((prev) => {
      const arr = Array.isArray(prev) ? prev : [];
      return arr.includes(key) ? arr.filter((p) => p !== key) : [...arr, key];
    });
  }

  function selectAll() {
    const all = [];
    for (const g of groups) {
      for (const p of g.perms || g.permissions || []) all.push(p.key || p);
    }
    setSelected(Array.from(new Set(all)));
  }

  function clearAll() {
    setSelected([]);
  }

  async function save() {
    if (!userId) return;
    setSaving(true);
    try {
      await api.put(`/users/${userId}/permissions`, { permissions: selected });
      toast.success('تم حفظ الصلاحيات');
      qc.invalidateQueries({ queryKey: ['users', userId, 'permissions'] });
      qc.invalidateQueries({ queryKey: ['users'] });
      onClose?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: 'rgba(15,16,18,0.5)' }}
    >
      <div
        className="bg-white rounded-[14px] shadow-card w-[820px] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-h">
          <div className="flex-1">
            <h3 className="text-[15px] font-bold">صلاحيات المستخدم</h3>
            <div className="text-xs text-ink-3 mt-0.5">
              {user?.fullName || user?.username || ''} — مفعّل: {selected.length}/{totalCount}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" className="btn" onClick={selectAll} disabled={saving}>
              تحديد الكل
            </button>
            <button type="button" className="btn" onClick={clearAll} disabled={saving}>
              إفراغ
            </button>
            <button type="button" className="text-ink-3 hover:text-ink" onClick={onClose}>
              <Icon name="x" size={16} />
            </button>
          </div>
        </div>

        <div className="p-4 overflow-y-auto flex-1">
          {userPermsQuery.isLoading ? (
            <div className="text-center py-10 text-ink-3 text-sm">جاري تحميل الصلاحيات...</div>
          ) : (
            <PermissionMatrix groups={groups} selected={selected} onToggle={toggle} disabled={saving} />
          )}
        </div>

        <div className="card-h border-b-0 border-t flex gap-2 justify-end">
          <button type="button" className="btn" onClick={onClose} disabled={saving}>
            إلغاء
          </button>
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            <Icon name="check" size={14} />
            {saving ? 'جاري الحفظ...' : 'حفظ الصلاحيات'}
          </button>
        </div>
      </div>
    </div>
  );
}
